<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  toRef,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  inject,
} from 'vue';
import { injectPageLoadTracker } from '@integro/core';
import { useVirtualizer } from '@tanstack/vue-virtual';
import type {
  ItpDataGridColumn,
  ItpDataGridDataProvider,
  GridCommandEvent,
  GridSelectionChangedEvent,
  ItpDataGridGroupBy,
  GridVisualRow,
} from './types';
import { gridMessages } from '../../messages';
import { useGridI18n } from './gridI18n';
import {
  useGridSort,
  useGridFilter,
  useGridSelection,
  useGridData,
  isConnectionError,
  type ItpDataGridItems,
} from './useGridRows';
import {
  useGridColumnLayout,
  useGridTracks,
  autoFitColumnWidth,
  layoutStorageKey,
  normalizeColumnWidths,
} from './useGridColumns';
import { useGridCells } from './useGridCells';
import { useGridScroll } from './useGridScroll';
import { useGridGrouping } from './useGridGrouping';
import { useGridPreferences } from './gridPreferences';
import { isSubtotalColumn } from './gridAggregates';
import {
  isCommandOnlyAnchor,
  resolveCommandTarget,
  columnKey,
  alignClass,
} from './gridCell';
import { downloadCsv, downloadXlsx } from './gridExport';
import ItpDataGridToolbar from './ItpDataGridToolbar.vue';
import ItpDataGridHeader from './ItpDataGridHeader.vue';
import ItpDataGridFilterRow from './ItpDataGridFilterRow.vue';
import ItpDataGridFooter from './ItpDataGridFooter.vue';
import ItpLoadError from '../loading/ItpLoadError.vue';
import './styles.scss';

// ── Props & emits ──
const props = withDefaults(
  defineProps<{
    columns?: ItpDataGridColumn[];
    items?: ItpDataGridItems;
    rowHeight?: number;
    height?: number;
    overscan?: number;
    rowKey?: string;
    dynamicRowHeight?: boolean;
    fastScrollSkeleton?: boolean;
    skeletonVelocity?: number;
    totalCount?: number;
    dataProvider?: ItpDataGridDataProvider | null;
    columnDefaults?: Partial<ItpDataGridColumn>;
    headerTitle?: string;
    showToolbar?: boolean;
    showFilterRow?: boolean;
    headerShowRowCount?: boolean;
    headerShowReloadButton?: boolean;
    exportToCsv?: boolean;
    exportToCsvFilename?: string;
    filterDebounceMs?: number;
    checkboxRowSelection?: boolean;
    rowHeader?: boolean;
    footerTotals?: boolean;
    loading?: boolean;
    reloadIcon?: string;
    exportIcon?: string;
    name?: string;
    stateKey?: string;
    resizableColumns?: boolean;
    reorderableColumns?: boolean;
    showColumnPicker?: boolean;
    groupBy?: ItpDataGridGroupBy;
  }>(),
  {
    columns: () => [],
    items: undefined,
    rowHeight: undefined,
    height: 560,
    overscan: 8,
    rowKey: 'id',
    dynamicRowHeight: false,
    fastScrollSkeleton: true,
    skeletonVelocity: 15000,
    totalCount: 0,
    dataProvider: null,
    columnDefaults: () => ({}),
    headerTitle: '',
    showToolbar: true,
    showFilterRow: true,
    headerShowRowCount: true,
    headerShowReloadButton: true,
    exportToCsv: true,
    exportToCsvFilename: 'export',
    filterDebounceMs: 250,
    checkboxRowSelection: false,
    rowHeader: false,
    footerTotals: false,
    loading: false,
    reloadIcon: 'ooui:reload',
    exportIcon: 'mdi:download',
    name: undefined,
    stateKey: undefined,
    resizableColumns: true,
    reorderableColumns: true,
    showColumnPicker: true,
    groupBy: undefined,
  },
);

const emit = defineEmits<{
  refresh: [];
  command: [event: GridCommandEvent];
  selectedRowsChanged: [event: GridSelectionChangedEvent];
}>();

// ── Columns: defined (page YAML) → layout (saved order/width) → visible ──
const definedColumns = computed<ItpDataGridColumn[]>(() => {
  const defaults = props.columnDefaults;
  const withDefaults =
    defaults && Object.keys(defaults).length > 0
      ? (column: ItpDataGridColumn) => ({ ...defaults, ...column })
      : (column: ItpDataGridColumn) => column;
  return props.columns
    .filter((column) => column.if !== false)
    .map((column) => normalizeColumnWidths(withDefaults(column)));
});

const {
  layoutColumns,
  setWidth,
  setWidths,
  setHidden,
  hasHiddenColumns,
  clearHidden,
  moveColumn,
  resetLayout,
  saveScroll,
  initialScroll,
} = useGridColumnLayout(
  definedColumns,
  toRef(props, 'name'),
  toRef(props, 'stateKey'),
);

const visibleColumns = computed(() =>
  layoutColumns.value.filter((column) => !column.hidden),
);

// ── Rows: locale, data source, sort, filter ──
const localMode = computed(() => !props.dataProvider);

const { locale, dateFormat, excelDateFormat, formatNumber, formatDate } =
  useGridI18n();

const { resolveRowHeight } = useGridPreferences();
const rowHeight = resolveRowHeight(toRef(props, 'rowHeight'));

const loadTracker = injectPageLoadTracker();
const insideModal = inject<boolean>('itp-inside-modal', false);
const joinPageReload = () =>
  loadTracker?.reloading.value ? loadTracker.reloadStarted() : undefined;

const {
  rows: sourceRows,
  loading: isLoading,
  load: loadItems,
  ownsData,
  error: loadError,
  stale: showingStaleRows,
} = useGridData(toRef(props, 'items'), toRef(props, 'loading'), joinPageReload);

const showLoadingOverlay = computed(
  () => isLoading.value && (insideModal || !loadTracker?.reloading.value),
);

const showLoadError = computed(
  () => !!loadError.value && !showingStaleRows.value && !isLoading.value,
);

const staleWarning = computed(() =>
  isConnectionError(loadError.value)
    ? gridMessages.refreshFailedOffline
    : gridMessages.refreshFailed,
);

const displayError = computed<Error | string | null>(() => {
  const error = loadError.value;
  if (error == null) {
    return null;
  }
  return error instanceof Error || typeof error === 'string'
    ? error
    : String(error);
});

const { formatterFor, cellText, formatterText, isCommandCell, buildCells } =
  useGridCells({ columns: visibleColumns, formatNumber, formatDate });

function totalText(column: ItpDataGridColumn, value: number): string {
  const toText = formatterFor(column)?.toText;
  return toText ? toText(value, {}) : formatNumber(value);
}

const {
  sortEntries,
  cycleSort,
  clearSort,
  headerSortState,
  isSortable,
  comparator,
} = useGridSort(layoutColumns, locale, formatterText);

const {
  filters,
  setFilter,
  clearFilters,
  isFilterable,
  hasActiveFilters,
  predicate,
} = useGridFilter(layoutColumns, cellText);

const filterDrafts = reactive<Record<string, string>>({});
let filterCommitTimer: ReturnType<typeof setTimeout> | undefined;

function onFilterInput(field: string, value: string): void {
  filterDrafts[field] = value;
  clearTimeout(filterCommitTimer);
  filterCommitTimer = setTimeout(commitFilters, props.filterDebounceMs);
}

function commitFilters(): void {
  for (const field of Object.keys(filterDrafts)) {
    setFilter(field, filterDrafts[field]);
  }
}

const filteredRows = computed<readonly any[]>(() => {
  const match = predicate.value;
  const rows = sourceRows.value;
  return match ? rows.filter(match) : rows;
});

const displayRows = computed<readonly any[] | null>(() => {
  if (!localMode.value) {
    return null;
  }
  const rows = filteredRows.value;
  const compare = comparator.value;
  if (!compare) {
    return rows;
  }
  return [...rows].sort(compare);
});

// ── Selection & subtotals ──
const selectableRows = computed<readonly any[]>(() => displayRows.value ?? []);
const {
  selectedKeys,
  isSelected,
  toggle: toggleRow,
  toggleAll,
  clear: clearSelection,
  selectedCount,
  selectedIds,
  selectedItems,
  fromSelection,
  allSelected,
  someSelected,
  subtotalColumns,
  columnTotals,
} = useGridSelection(selectableRows, toRef(props, 'rowKey'), visibleColumns);

watch(selectedKeys, () => {
  emit('selectedRowsChanged', { selectedIds: selectedIds.value });
});

// ── Grouping ──
const groupStorageKey = computed(() =>
  layoutStorageKey(props.name, props.stateKey),
);

const {
  isGrouped,
  visualRows,
  toggleGroup,
  hasCollapsedGroups,
  expandAll,
  resetGroupState,
} = useGridGrouping({
  rows: computed(() => displayRows.value ?? []),
  groupBy: toRef(props, 'groupBy'),
  locale,
  storageKey: groupStorageKey,
  subtotalColumns: computed(() => subtotalColumns.value),
});

const groupLabelSpan = computed(() => {
  const columns = visibleColumns.value;
  const firstTotal = columns.findIndex(isSubtotalColumn);
  return firstTotal === -1 ? columns.length : Math.max(firstTotal, 1);
});

const groupTotalColumns = computed(() =>
  visibleColumns.value.slice(groupLabelSpan.value),
);

const canSelect = computed(() => props.checkboxRowSelection && localMode.value);
const canExport = computed(() => props.exportToCsv && localMode.value);

const hasSystemColumn = computed(() => canSelect.value || props.rowHeader);
const showFooter = computed(
  () =>
    props.footerTotals && localMode.value && subtotalColumns.value.length > 0,
);

// ── Remote query sync (server-side sort/filter) ──
watch(
  [sortEntries, filters],
  ([entries, activeFilters]) => {
    if (!localMode.value && props.dataProvider?.setQuery) {
      props.dataProvider.setQuery({
        sort: entries,
        filters: { ...activeFilters },
      });
    }
  },
  { deep: true },
);

function onToggleColumn(key: string, visible: boolean): void {
  setHidden(key, visible);
}

// ── Toolbar actions & the reloadGrid action ──
const hasActiveQuery = computed(
  () =>
    sortEntries.value.length > 0 ||
    hasActiveFilters.value ||
    hasHiddenColumns.value ||
    hasCollapsedGroups.value,
);

function clearAll(): void {
  clearSort();
  clearFilters();
  clearHidden();
  resetGroupState();
  if (hasCollapsedGroups.value) {
    expandAll();
  }
  for (const key of Object.keys(filterDrafts)) {
    delete filterDrafts[key];
  }
}

function onRefresh(): void {
  props.dataProvider?.reload?.();
  if (ownsData.value) {
    void loadItems();
  }
  emit('refresh');
}

const gridEvents = () =>
  (getCurrentInstance()?.proxy as any)?.$app?.events ?? null;

function onGlobalReload(args: { grid: string | string[] }): void {
  const name = props.name;
  const target = args?.grid;
  if (!name || target == null) {
    return;
  }
  const matches = Array.isArray(target)
    ? target.includes(name)
    : target === 'all' || target === name;
  if (matches) {
    onRefresh();
  }
}

function onResourceDeleted(args: { id?: unknown }): void {
  const id = args?.id;
  if (id == null) {
    return;
  }
  const key = props.rowKey;
  if (sourceRows.value.some((row) => row?.[key] === id)) {
    onRefresh();
  }
}

onMounted(() => {
  gridEvents()?.on?.('grid:reload', onGlobalReload);
  gridEvents()?.on?.('resource:deleted', onResourceDeleted);
});
onBeforeUnmount(() => {
  gridEvents()?.off?.('grid:reload', onGlobalReload);
  gridEvents()?.off?.('resource:deleted', onResourceDeleted);
});

// ── Export: CSV / Excel ──
const exportRows = computed<readonly any[]>(() => {
  const rows = displayRows.value ?? [];
  return fromSelection.value ? rows.filter((row) => isSelected(row)) : rows;
});

const exporting = ref(false);

async function onExportCsv(): Promise<void> {
  if (exporting.value) {
    return;
  }
  exporting.value = true;
  try {
    await nextTick();
    await nextPaint();
    downloadCsv(exportRows.value, visibleColumns.value, {
      filename: props.exportToCsvFilename,
      locale: locale.value,
      dateFormat: dateFormat.value,
      formatterFor,
    });
  } catch (error) {
    console.error('[ItpDataGrid] CSV export failed', error);
  } finally {
    exporting.value = false;
  }
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

async function onExportXlsx(): Promise<void> {
  if (exporting.value) {
    return;
  }
  exporting.value = true;
  try {
    await nextTick();
    await nextPaint();
    await downloadXlsx(exportRows.value, visibleColumns.value, {
      filename: props.exportToCsvFilename,
      dateFormat: excelDateFormat.value,
      formatterFor,
    });
  } catch (error) {
    console.error('[ItpDataGrid] Excel export failed', error);
  } finally {
    exporting.value = false;
  }
}

onBeforeUnmount(() => clearTimeout(filterCommitTimer));

// ── Sorting & column auto-fit interactions ──
function onHeaderClick(column: ItpDataGridColumn): void {
  cycleSort(columnKey(column));
}

const gridRoot = ref<HTMLElement | null>(null);

const TRUNCATABLE =
  '.itp-data-grid__cell, .itp-data-grid__fcell, .itp-data-grid__hlabel';

function isClipped(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  const available =
    element.clientWidth -
    parseFloat(style.paddingLeft || '0') -
    parseFloat(style.paddingRight || '0');

  const range = document.createRange();
  range.selectNodeContents(element);

  return range.getBoundingClientRect().width > available + 1;
}

function showTitleWhenClipped(event: MouseEvent): void {
  const target = (event.target as HTMLElement | null)?.closest?.(
    TRUNCATABLE,
  ) as HTMLElement | null;

  if (!target || target.dataset.ownTitle !== undefined) {
    return;
  }

  const text = target.textContent?.trim() ?? '';

  if (text && isClipped(target)) {
    if (target.title !== text) {
      target.title = text;
    }
  } else if (target.hasAttribute('title')) {
    target.removeAttribute('title');
  }
}

function onAutoFitColumn(field: string): void {
  const column = layoutColumns.value.find((item) => columnKey(item) === field);
  if (!column) {
    return;
  }
  const width = autoFitColumnWidth({
    column,
    position: visibleColumns.value.indexOf(column),
    hasSystemColumn: hasSystemColumn.value,
    hasComponent: !!formatterFor(column)?.component,
    root: gridRoot.value,
    rows: localMode.value ? (displayRows.value ?? []) : [],
    cellText,
  });
  if (width !== null) {
    setWidth(field, width);
  }
}

// ── Row counts ──
const rowCount = computed(() =>
  localMode.value ? (displayRows.value?.length ?? 0) : props.totalCount,
);

const renderedRowCount = computed(() =>
  isGrouped.value && localMode.value ? visualRows.value.length : rowCount.value,
);

const countLabel = computed(() => {
  const shown = formatNumber(rowCount.value);
  if (localMode.value && hasActiveFilters.value) {
    return `${shown} ${gridMessages.of} ${formatNumber(sourceRows.value.length)}`;
  }
  return shown;
});

// ── Virtualization: track sizing, scrolling, and the rows actually drawn ──
const filterRowEl = ref<{ $el?: HTMLElement } | null>(null);
const filterRowHeight = ref(0);

function measureFilterRow(): void {
  const element = filterRowEl.value?.$el;
  filterRowHeight.value =
    element instanceof HTMLElement ? element.offsetHeight : 0;
}

watch(
  [visibleColumns, () => props.showFilterRow],
  () => void nextTick(measureFilterRow),
  { immediate: true },
);
onMounted(measureFilterRow);

const { gridTemplateColumns, frameStyle } = useGridTracks(
  visibleColumns,
  hasSystemColumn,
  toRef(props, 'rowHeader'),
);

const { scrollElement, frameEl, isFastScrolling, handleScroll } = useGridScroll(
  {
    rowCount: renderedRowCount,
    saveScroll,
    initialScroll,
    fastScrollSkeleton: toRef(props, 'fastScrollSkeleton'),
    skeletonVelocity: toRef(props, 'skeletonVelocity'),
  },
);

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: renderedRowCount.value,
    getScrollElement: () => scrollElement.value,
    estimateSize: () => rowHeight.value,
    overscan: props.overscan,
    scrollMargin: filterRowHeight.value,
  })),
);

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

function measureRow(element: Element | null): void {
  if (element) {
    rowVirtualizer.value.measureElement(element);
  }
}

function rowStyle(virtualRow: {
  start: number;
  size: number;
}): Record<string, string> {
  const style: Record<string, string> = {
    transform: `translateY(${virtualRow.start - filterRowHeight.value}px)`,
    gridTemplateColumns: gridTemplateColumns.value,
  };
  if (!props.dynamicRowHeight) {
    style.height = `${virtualRow.size}px`;
  }
  return style;
}

function visualAt(index: number): GridVisualRow | undefined {
  return visualRows.value[index];
}

function rowAt(index: number): any {
  if (!localMode.value) {
    return props.dataProvider!.getRow(index);
  }
  if (isGrouped.value) {
    const entry = visualAt(index);
    return entry?.kind === 'data' ? entry.row : undefined;
  }
  return displayRows.value?.[index];
}

function isSkeletonRow(row: any): boolean {
  if (isFastScrolling.value) {
    return true;
  }
  return localMode.value ? false : row === undefined;
}

const renderRows = computed(() =>
  virtualRows.value.map((virtualRow) => {
    const entry =
      isGrouped.value && localMode.value ? visualAt(virtualRow.index) : null;
    if (entry?.kind === 'group') {
      return {
        virtualRow,
        row: null,
        group: entry.group,
        key: `g:${entry.group.key}`,
        skeleton: false,
        cells: null,
      };
    }
    const row = entry?.kind === 'data' ? entry.row : rowAt(virtualRow.index);
    const key = props.dataProvider
      ? `i:${virtualRow.index}`
      : (row?.[props.rowKey] ?? `i:${virtualRow.key}`);
    const skeleton = isSkeletonRow(row);
    return {
      virtualRow,
      row,
      group: null,
      key,
      skeleton,
      cells: skeleton ? null : buildCells(row),
    };
  }),
);

function fetchVisibleRange(): void {
  const provider = props.dataProvider;
  if (!provider) {
    return;
  }
  const rows = virtualRows.value;
  if (rows.length) {
    provider.ensureRange(rows[0].index, rows[rows.length - 1].index);
  }
}

watch(
  [
    () => props.dataProvider,
    () => {
      const rows = virtualRows.value;
      return rows.length
        ? `${rows[0].index}:${rows[rows.length - 1].index}`
        : '';
    },
  ],
  () => {
    if (!isFastScrolling.value) {
      fetchVisibleRange();
    }
  },
  { immediate: true },
);

watch(isFastScrolling, (fastScrolling) => {
  if (!fastScrolling) {
    fetchVisibleRange();
  }
});

// ── Command clicks ──
function onGridClick(event: MouseEvent): void {
  const target = resolveCommandTarget(
    event.target,
    visibleColumns.value,
    isCommandCell,
  );
  if (!target) {
    return;
  }
  const row = rowAt(target.index);
  if (row == null) {
    return;
  }
  if (!isCommandOnlyAnchor(target.anchor)) {
    return;
  }
  event.preventDefault();
  emit('command', {
    column: { ...target.column, id: columnKey(target.column) },
    row: target.index,
    data: row,
    element: target.anchor,
    value: target.anchor.dataset.value,
  });
}

// ── Exposed API ($refs.grid) ──
defineExpose({
  get renderedRowCount() {
    return virtualRows.value.length;
  },
  scrollToIndex: (index: number) => rowVirtualizer.value.scrollToIndex(index),
  reload: onRefresh,
  resetLayout,
  getSelectedItems: () => selectedItems.value,
  clearSelection,
});
</script>

<template>
  <div ref="gridRoot" class="itp-data-grid" @mouseover="showTitleWhenClipped">
    <ItpDataGridToolbar
      v-if="showToolbar"
      :header-title="headerTitle"
      :count-label="countLabel"
      :show-row-count="headerShowRowCount"
      :has-active-query="hasActiveQuery"
      :can-export="canExport"
      :show-refresh="headerShowReloadButton"
      :exporting="exporting"
      :loading="isLoading"
      :reload-icon="reloadIcon"
      :export-icon="exportIcon"
      :columns="layoutColumns"
      :show-column-picker="showColumnPicker"
      @clear="clearAll"
      @refresh="onRefresh"
      @export-csv="onExportCsv"
      @export-xlsx="onExportXlsx"
      @toggle-column="onToggleColumn"
    >
      <template #toolbar>
        <slot
          name="toolbar"
          :selected-row-count="selectedCount"
          :selected-ids="selectedIds"
        />
      </template>
    </ItpDataGridToolbar>

    <div
      ref="frameEl"
      class="itp-data-grid__frame"
      role="grid"
      :style="frameStyle"
    >
      <div
        v-if="showingStaleRows"
        class="itp-data-grid__stalebanner"
        role="alert"
      >
        <span>{{ staleWarning }}</span>
        <button type="button" class="itp-data-grid__retry" @click="onRefresh">
          {{ gridMessages.reload }}
        </button>
      </div>

      <ItpDataGridHeader
        :columns="visibleColumns"
        :grid-template-columns="gridTemplateColumns"
        :has-system-column="hasSystemColumn"
        :can-select="canSelect"
        :all-selected="allSelected"
        :some-selected="someSelected"
        :is-sortable="isSortable"
        :sort-state="headerSortState"
        :resizable="resizableColumns"
        :reorderable="reorderableColumns"
        @toggle-all="toggleAll"
        @sort-column="onHeaderClick"
        @resize-column="setWidth"
        @freeze-columns="setWidths"
        @move-column="moveColumn"
        @autofit-column="onAutoFitColumn"
      />

      <div
        ref="scrollElement"
        class="itp-data-grid__viewport"
        :style="{ height: `${height}px` }"
        @scroll="handleScroll"
      >
        <ItpDataGridFilterRow
          v-if="showFilterRow"
          ref="filterRowEl"
          :columns="visibleColumns"
          :grid-template-columns="gridTemplateColumns"
          :has-system-column="hasSystemColumn"
          :drafts="filterDrafts"
          :is-filterable="isFilterable"
          @input="onFilterInput"
        />

        <div
          class="itp-data-grid__sizer"
          :style="{ height: `${totalSize}px` }"
          @click="onGridClick"
        >
          <div
            v-for="renderRow in renderRows"
            :key="renderRow.key"
            :ref="dynamicRowHeight ? measureRow : undefined"
            :data-index="renderRow.virtualRow.index"
            class="itp-data-grid__row"
            role="row"
            :class="
              renderRow.group
                ? [
                    'itp-data-grid__grouprow',
                    `is-level-${renderRow.group.level}`,
                  ]
                : { 'is-selected': canSelect && isSelected(renderRow.row) }
            "
            :style="rowStyle(renderRow.virtualRow)"
          >
            <template v-if="renderRow.group">
              <div
                v-if="hasSystemColumn"
                class="itp-data-grid__cell itp-data-grid__syscell"
              />
              <div
                class="itp-data-grid__cell itp-data-grid__grouplabel"
                :style="{
                  gridColumn: `span ${groupLabelSpan}`,
                  '--itp-group-level': renderRow.group.level,
                }"
                role="button"
                tabindex="0"
                :aria-expanded="!renderRow.group.collapsed"
                :title="renderRow.group.label"
                @click="toggleGroup(renderRow.group.key)"
                @keydown.enter.prevent="toggleGroup(renderRow.group.key)"
                @keydown.space.prevent="toggleGroup(renderRow.group.key)"
              >
                <span
                  class="itp-data-grid__groupchevron"
                  :class="{ 'is-open': !renderRow.group.collapsed }"
                  aria-hidden="true"
                  >▶</span
                >
                <span class="itp-data-grid__grouptext">{{
                  renderRow.group.label
                }}</span>
                <span class="itp-data-grid__groupcount"
                  >({{ formatNumber(renderRow.group.count) }})</span
                >
              </div>
              <div
                v-for="column in groupTotalColumns"
                :key="columnKey(column)"
                class="itp-data-grid__cell itp-data-grid__grouptotal"
                :class="alignClass(column)"
              >
                <span
                  v-if="renderRow.group.totals[columnKey(column)] !== undefined"
                  class="itp-data-grid__text"
                  >{{
                    totalText(column, renderRow.group.totals[columnKey(column)])
                  }}</span
                >
              </div>
            </template>
            <template v-else>
              <div
                v-if="hasSystemColumn"
                class="itp-data-grid__cell itp-data-grid__syscell"
              >
                <input
                  v-if="canSelect"
                  type="checkbox"
                  class="itp-data-grid__rowcheck"
                  :checked="isSelected(renderRow.row)"
                  @change="toggleRow(renderRow.row)"
                />
                <span v-if="rowHeader" class="itp-data-grid__rownum">{{
                  renderRow.virtualRow.index + 1
                }}</span>
              </div>
              <template v-if="renderRow.skeleton">
                <div
                  v-for="column in visibleColumns"
                  :key="columnKey(column)"
                  class="itp-data-grid__cell"
                  :data-field="columnKey(column)"
                >
                  <span class="itp-data-grid__skeleton" aria-hidden="true" />
                </div>
              </template>
              <div
                v-for="cell in renderRow.cells"
                v-else
                :key="cell.key"
                class="itp-data-grid__cell"
                :class="cell.class"
                :data-field="cell.key"
              >
                <component
                  :is="cell.component"
                  v-if="cell.component"
                  v-bind="cell.props"
                  >{{ cell.text }}</component
                >
                <a
                  v-else-if="cell.command"
                  href="#"
                  class="itp-data-grid__command"
                  >{{ cell.text }}</a
                >
                <span v-else class="itp-data-grid__text">{{ cell.text }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <ItpDataGridFooter
        v-if="showFooter"
        :columns="visibleColumns"
        :grid-template-columns="gridTemplateColumns"
        :has-system-column="hasSystemColumn"
        :column-totals="columnTotals"
        :format-total="totalText"
        :from-selection="fromSelection"
        :selected-count="selectedCount"
      />

      <ItpLoadError
        v-if="showLoadError"
        class="itp-data-grid__loaderror"
        :error="displayError"
        :reloading="isLoading"
        @reload="onRefresh"
      />

      <div
        v-if="showLoadingOverlay"
        class="itp-data-grid__loading"
        aria-live="polite"
        aria-busy="true"
      >
        <span class="itp-data-grid__spinner" aria-hidden="true" />
      </div>
    </div>
  </div>
</template>
